import './WidgetStyle.css'; 
import './ResponsiveFonts.css';
import './SupremeTestimonialAnimations.css';
import '../WidgetEditComponents/TestimonialCardStyles.css';
import { useFont } from '../../Design/FontContext';
import React, { useState, useEffect, useRef } from 'react';
import DeleteModal from './DeleteModal';
import { getTextEffectStyles } from '../utils/textEffectsUtils';
import '../WidgetEditComponents/TextEffectsAnimations.css';
import { generateLayoutStyles, getResponsiveValue, appendPx } from '../WidgetEditComponents/LayoutUtils';
import AIRefreshIcon from '../../components/AIRefreshIcon';

// appendPx wird jetzt aus LayoutUtils importiert

function SupremeTestimonial({ data, onRemove, isSelected, isEditMode, onUpdate }) {
    const defaultBackgroundImage = '/img/crowd.jpg';
    const defaultAvatar = '/img/blogger.webp';
    const deleteLogo = '/img/delete.svg';

    const fonts = useFont();

    function isDark(color) {
        const rgb = parseInt(color.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        return luma < 128;
    }

    function getBorderStyle() {
        if (isEditMode) {
            if (isSelected) {
                const borderColor = isDark(data.backgroundColor) ? '#FFFFFF' : '#000000';
                return `2px dashed ${borderColor}`;
            } else {
                return data.border ? `1.5px solid ${data.borderColor}` : '2px dashed transparent';
            }
        } else {
            return data.border ? `1.5px solid ${data.borderColor}` : 'none';
        }
    }

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isMobileLayout, setIsMobileLayout] = useState(false);

    // 🚀 RESPONSIVE LAYOUT STYLES - Mit Revolutionary Layout System
    const layoutStyles = generateLayoutStyles(data, isMobileLayout, {
        backgroundColor: data.backgroundColor || '#ffffff',
        border: getBorderStyle(),
        borderRadius: appendPx(data.borderRadius),
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        maxWidth: 'none',
        display: 'flex'
    });

    const widgetStyle = {
        ...layoutStyles,
        // Fallback für alte Properties (Backwards Compatibility)
        ...(layoutStyles.height === 'auto' && !layoutStyles.minHeight && {
            minHeight: appendPx(data.height || 600)
        })
    };
    const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
    const containerRef = useRef(null);
    const cardsContainerRef = useRef(null);

    // Dynamische Breakpoint-Erkennung basierend auf Container-Breite
    useEffect(() => {
        const checkContainerWidth = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                setIsMobileLayout(containerWidth < 750);
            }
        };

        checkContainerWidth();
        window.addEventListener('resize', checkContainerWidth);
        
        const observer = new ResizeObserver(checkContainerWidth);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            window.removeEventListener('resize', checkContainerWidth);
            observer.disconnect();
        };
    }, []);

    // Scroll zur aktiven Card bei Mobile Layout
    useEffect(() => {
        if (isMobileLayout && data.testimonials) {
            setTimeout(() => {
                const container = cardsContainerRef.current;
                if (!container) return;
                
                const cardWidth = 250;
                const gap = 20;
                const padding = 40;
                const containerWidth = container.offsetWidth;
                
                const cardPosition = padding + (activeTestimonialIndex * (cardWidth + gap));
                const targetScrollPosition = cardPosition - (containerWidth / 2) + (cardWidth / 2);
                const maxScroll = container.scrollWidth - containerWidth;
                const scrollPosition = Math.max(0, Math.min(maxScroll, targetScrollPosition));
                
                container.scrollTo({
                    left: scrollPosition,
                    behavior: 'smooth'
                });
            }, 150);
        }
    }, [activeTestimonialIndex, isMobileLayout, data.testimonials]);

    // Auto-Rotation
    useEffect(() => {
        if (data.testimonials && data.testimonials.length > 1) {
            const interval = setInterval(() => {
                setActiveTestimonialIndex(prev => 
                    prev >= data.testimonials.length - 1 ? 0 : prev + 1
                );
            }, 5000);

            return () => clearInterval(interval);
        }
    }, [data.testimonials]);

    const activeTestimonial = data.testimonials && data.testimonials.length > 0 
        ? data.testimonials[activeTestimonialIndex] 
        : null;

    const handleDelete = (event) => {
        onRemove(event, data.id);
        setShowDeleteModal(false);
    };

    // Layout Configuration basierend auf layoutType
    const getLayoutConfig = () => {
        const layoutType = data.layoutType || 'testimonial-cards';
        
        switch (layoutType) {
            case 'testimonial-cards':
                return { displayType: 'cards', showCards: true, showQuote: true };
            case 'simple-names-bottom':
                return { displayType: 'simple-names', showCards: false, showQuote: true };
            case 'quote-only':
                return { displayType: 'quote-only', showCards: false, showQuote: true };
            case 'minimal-attribution':
                return { displayType: 'minimal', showCards: false, showQuote: true };
            case 'side-by-side':
                return { displayType: 'side-by-side', showCards: false, showQuote: true };
            case 'inline-below-quote':
                return { displayType: 'inline-below', showCards: false, showQuote: true };
            case 'avatar-carousel':
                return { displayType: 'avatar-carousel', showCards: false, showQuote: true };
            case 'single-avatar-focus':
                return { displayType: 'single-avatar', showCards: false, showQuote: true };
            default:
                return { displayType: 'cards', showCards: true, showQuote: true };
        }
    };

    const layoutConfig = getLayoutConfig();

    const getInitials = (name) => {
        if (!name) return 'XX';
        return name.split(' ')
            .map(word => word.charAt(0))
            .join('')
            .substring(0, 2)
            .toUpperCase();
    };

    const getImageEffect = (effect) => {
        switch (effect) {
            case 'blur':
                return { filter: 'blur(3px)', gradient: null };
            case 'blur-light':
                return { filter: 'blur(1px)', gradient: null };
            case 'blur-heavy':
                return { filter: 'blur(5px)', gradient: null };
            case 'grayscale':
                return { filter: 'grayscale(100%)', gradient: null };
            case 'top-fade':
                return { filter: 'none', gradient: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent)' };
            case 'bottom-fade':
                return { filter: 'none', gradient: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' };
            case 'left-fade':
                return { filter: 'none', gradient: 'linear-gradient(to right, rgba(0,0,0,0.6), transparent)' };
            case 'right-fade':
                return { filter: 'none', gradient: 'linear-gradient(to left, rgba(0,0,0,0.6), transparent)' };
            case 'center-glow':
                return { filter: 'none', gradient: 'radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.6))' };
            case 'dark-vignette':
                return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(0,0,0,0.7))' };
            case 'light-vignette':
                return { filter: 'none', gradient: 'radial-gradient(circle, transparent 30%, rgba(255,255,255,0.7))' };
            default:
                return { filter: 'none', gradient: null };
        }
    };

    return (
        <div 
            className="widget-container supreme-testimonial-widget" 
            style={widgetStyle}
            ref={containerRef}
        >
            {/* Delete Button */}
            {isEditMode && (
                <button className="delete_button" onClick={() => setShowDeleteModal(true)}>
                    <img className="delete_logo" src={deleteLogo} alt="Löschen" />
                </button>
            )}
            
            {/* 🤖 AI REFRESH ICON */}
            {isEditMode && (
                <AIRefreshIcon 
                    widget={{
                        ...data,
                        type: 'SupremeTestimonial'
                    }}
                    spaceId={null}
                    onContentRefreshed={(refreshedWidget) => {
                        console.log('🤖 [SUPREMETESTIMONIAL] AI-refreshed content received:', refreshedWidget);
                        onUpdate(refreshedWidget);
                    }}
                    position="top-left"
                />
            )}
            {isEditMode && showDeleteModal && (
                <DeleteModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={(event) => handleDelete(event, data.id)}
                />
            )}
            
            {/* Background System */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1,
                backgroundColor: data.backgroundColor || '#4A90E2'
            }}>
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${activeTestimonial?.backgroundImage || data.backgroundImage || defaultBackgroundImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: data.backgroundImageOpacity || 0.8,
                    filter: data.imageEffect ? getImageEffect(data.imageEffect).filter : 'none',
                    transition: 'opacity 0.4s ease-in-out',
                    zIndex: 1
                }} />
                
                {data.imageEffect && getImageEffect(data.imageEffect).gradient && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: getImageEffect(data.imageEffect).gradient,
                        zIndex: 2
                    }} />
                )}
            </div>

            {/* Quote Container - Obere 70% */}
            <div className="supreme-testimonial-quote-container" style={{
                position: 'absolute',
                top: layoutConfig.displayType === 'quote-only' ? '15%' : '0', // Quote Only: 15% nach unten
                left: '0',
                right: '0',
                height: layoutConfig.displayType === 'quote-only' ? '55%' : '70%', // Quote Only: weniger Höhe
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                {/* Quote wird bei ALLEN Layouts angezeigt - wenn showQuote true */}
                {layoutConfig.showQuote && data.testimonials && data.testimonials.map((testimonial, index) => (
                    <blockquote 
                        key={`quote-${index}`}
                        className="supreme-testimonial-quote-text"
                        style={{
                            fontSize: appendPx(data.quoteSize || 36),
                            fontWeight: data.quoteWeight || '400',
                            fontFamily: data.quoteFont || fonts.globalBigFont,
                            color: data.quoteColor || '#ffffff',
                            lineHeight: '1.5',
                            margin: '0',
                            fontStyle: 'italic',
                            textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                            maxWidth: '800px',
                            textAlign: 'center',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '90%',
                            opacity: index === activeTestimonialIndex ? 1 : 0,
                            transition: 'opacity 0.5s ease',
                            ...getTextEffectStyles(data.quoteEffect),
                            '--mobile-size': appendPx(data.quoteSizeMobile || 28),
                            '--normal-size': appendPx(data.quoteSize || 36),
                            '--big-size': appendPx(data.quoteSizeBig || 42)
                        }}>
                        "{testimonial.quote}"
                    </blockquote>
                ))}
            </div>

            {/* Attribution Container - Untere 30% */}
            <div className="supreme-testimonial-attribution-container" style={{
                position: 'absolute',
                top: '70%',
                left: '0',
                right: '0',
                bottom: '0',
                marginBottom: '0px',
                zIndex: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}>
                {/* Testimonials Karten */}
                {layoutConfig.showCards && data.testimonials && data.testimonials.length > 0 && (
                    <div 
                        ref={isMobileLayout ? cardsContainerRef : null}
                        style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            gap: isMobileLayout ? '20px' : '16px',
                            justifyContent: isMobileLayout ? 'flex-start' : 'center',
                            alignItems: 'center',
                            flexWrap: 'nowrap',
                            overflowX: isMobileLayout ? 'scroll' : 'visible',
                            paddingLeft: isMobileLayout ? '20px' : '0',
                            paddingRight: isMobileLayout ? '20px' : '0',
                            scrollBehavior: 'smooth',
                            scrollbarWidth: isMobileLayout ? 'none' : 'auto',
                            msOverflowStyle: isMobileLayout ? 'none' : 'auto'
                        }}>
                        {data.testimonials.map((testimonial, index) => {
                            const cardClass = index === activeTestimonialIndex ? 'supreme-card-active' : 'supreme-card-inactive';

                            return (
                                <div 
                                    key={index} 
                                    className={`supreme-testimonial-card card-style-${data.cardStyle || 'classic'} ${cardClass}`}
                                    style={{
                                        cursor: !isEditMode ? 'pointer' : 'default',
                                        width: isMobileLayout ? '250px' : '140px',
                                        flex: isMobileLayout ? '0 0 250px' : 'none',
                                        marginBottom: '40px',
                                        ...(isMobileLayout ? {
                                            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                                            opacity: index === activeTestimonialIndex ? 1 : 0.4,
                                            transform: index === activeTestimonialIndex ? 'scale(1.05)' : 'scale(0.85)',
                                            filter: index === activeTestimonialIndex ? 'blur(0px)' : 'blur(2px)',
                                            ...(data.cardStyle === 'ruby-elegant' ? {
                                                background: index === activeTestimonialIndex 
                                                    ? 'linear-gradient(135deg, #9c2020, #A52A2A, #771717, #CD5C5C)' 
                                                    : 'rgba(150, 150, 150, 0.3)',
                                                color: index === activeTestimonialIndex ? '#FFE4E1' : '#C88888'
                                            } : {})
                                        } : {})
                                    }}
                                    data-initials={getInitials(testimonial.name)}
                                    onClick={() => {
                                        if (!isEditMode) {
                                            setActiveTestimonialIndex(index);
                                            if (testimonial.website) {
                                                window.open(testimonial.website, '_blank');
                                            }
                                        }
                                    }}
                                >
                                    {!['text-only', 'initials'].includes(data.cardStyle) && (
                                        <img 
                                            src={testimonial.avatar || defaultAvatar}
                                            alt={testimonial.name}
                                            className={`avatar-transition ${index === activeTestimonialIndex ? 'avatar-active' : ''}`}
                                            style={{
                                                width: appendPx(data.avatarSize || 60),
                                                height: appendPx(data.avatarSize || 60),
                                                borderRadius: '50%',
                                                objectFit: 'cover',
                                                marginBottom: '12px',
                                                boxShadow: index === activeTestimonialIndex ? '0 6px 16px rgba(157, 157, 157, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.15)'
                                            }}
                                        />
                                    )}
                                    
                                    <h4 style={{
                                        fontSize: appendPx(data.nameSize || 14),
                                        fontWeight: data.nameWeight || '600',
                                        fontFamily: data.nameFont || fonts.globalSmallFont,
                                        color: isMobileLayout && data.cardStyle === 'ruby-elegant' && index === activeTestimonialIndex ? '#FFE4E1' : (data.nameColor || '#2c3e50'),
                                        margin: '0 0 6px 0',
                                        lineHeight: '1.3'
                                    }}>
                                        {testimonial.name}
                                    </h4>
                                    
                                    {testimonial.info && (
                                        <p style={{
                                            fontSize: appendPx(data.infoSize || 10),
                                            fontWeight: data.infoWeight || '400',
                                            fontFamily: data.infoFont || fonts.globalSmallFont,
                                            color: isMobileLayout && data.cardStyle === 'ruby-elegant' && index === activeTestimonialIndex ? '#FFE4E1' : (data.infoColor || '#666666'),
                                            margin: '0',
                                            lineHeight: '1.3',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical'
                                        }}>
                                            {testimonial.info}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Simple Names Layout */}
                {layoutConfig.displayType === 'simple-names' && data.testimonials && data.testimonials.length > 0 && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: '15px'
                    }}>
                        {data.testimonials[activeTestimonialIndex]?.info && (
                            <p style={{
                                fontSize: appendPx(isMobileLayout ? (data.infoSize || 16) * 0.9 : (data.infoSize || 16)),
                                fontWeight: data.infoWeight || '400',
                                fontFamily: data.infoFont || fonts.globalSmallFont,
                                color: data.infoColor || '#cccccc',
                                margin: '0 0 15px 0',
                                maxWidth: '600px',
                                textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                                lineHeight: '1.4'
                            }}>
                                {data.testimonials[activeTestimonialIndex].info}
                            </p>
                        )}

                        <div style={{
                            display: 'flex',
                            gap: isMobileLayout ? '12px' : '20px',
                            justifyContent: 'center',
                            flexWrap: 'wrap'
                        }}>
                            {data.testimonials.map((testimonial, index) => (
                                <div 
                                    key={index}
                                    style={{
                                        textAlign: 'center',
                                        cursor: !isEditMode ? 'pointer' : 'default',
                                        opacity: index === activeTestimonialIndex ? 1 : 0.6,
                                        transition: 'opacity 0.3s ease',
                                        padding: isMobileLayout ? '6px 8px' : '8px 12px',
                                        borderRadius: '6px',
                                        backgroundColor: index === activeTestimonialIndex ? 'rgba(255,255,255,0.1)' : 'transparent'
                                    }}
                                    onClick={() => {
                                        if (!isEditMode) {
                                            setActiveTestimonialIndex(index);
                                        }
                                    }}
                                >
                                    <h4 style={{
                                        fontSize: appendPx(isMobileLayout ? (data.nameSize || 16) * 0.9 : (data.nameSize || 16)),
                                        fontWeight: data.nameWeight || '600',
                                        fontFamily: data.nameFont || fonts.globalSmallFont,
                                        color: data.nameColor || '#ffffff',
                                        margin: '0',
                                        textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
                                    }}>
                                        {testimonial.name}
                                    </h4>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Minimal Attribution */}
                {layoutConfig.displayType === 'minimal' && data.testimonials && data.testimonials.length > 0 && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center'
                    }}>
                        <p style={{
                            fontSize: appendPx(data.nameSize || 18),
                            fontWeight: data.nameWeight || '600',
                            fontFamily: data.nameFont || fonts.globalSmallFont,
                            color: data.nameColor || '#ffffff',
                            margin: '0',
                            textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
                        }}>
                            — {data.testimonials[activeTestimonialIndex]?.name}
                        </p>
                    </div>
                )}

                {/* Side by Side Layout */}
                {layoutConfig.displayType === 'side-by-side' && data.testimonials && data.testimonials.length > 0 && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: isMobileLayout ? 'column' : 'row',
                        alignItems: 'center',
                        justifyContent: isMobileLayout ? 'center' : 'space-between',
                        gap: isMobileLayout ? '15px' : '0'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobileLayout ? '12px' : '15px',
                            flexShrink: 0,
                            order: isMobileLayout ? 1 : 2
                        }}>
                            <div style={{ 
                                textAlign: isMobileLayout ? 'left' : 'right',
                                order: isMobileLayout ? 2 : 1
                            }}>
                                <h4 style={{
                                    fontSize: appendPx(isMobileLayout ? (data.nameSize || 18) * 0.9 : (data.nameSize || 18)),
                                    fontWeight: data.nameWeight || '600',
                                    fontFamily: data.nameFont || fonts.globalSmallFont,
                                    color: data.nameColor || '#ffffff',
                                    margin: '0 0 4px 0',
                                    textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
                                }}>
                                    {data.testimonials[activeTestimonialIndex]?.name}
                                </h4>
                                {data.testimonials[activeTestimonialIndex]?.info && (
                                    <p style={{
                                        fontSize: appendPx(isMobileLayout ? (data.infoSize || 14) * 0.9 : (data.infoSize || 14)),
                                        fontWeight: data.infoWeight || '400',
                                        fontFamily: data.infoFont || fonts.globalSmallFont,
                                        color: data.infoColor || '#cccccc',
                                        margin: '0',
                                        textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
                                    }}>
                                        {data.testimonials[activeTestimonialIndex].info}
                                    </p>
                                )}
                            </div>
                            {data.testimonials[activeTestimonialIndex]?.avatar && (
                                <img 
                                    src={data.testimonials[activeTestimonialIndex].avatar}
                                    alt={data.testimonials[activeTestimonialIndex].name}
                                    style={{
                                        width: appendPx(isMobileLayout ? (data.avatarSize || 60) * 0.8 : (data.avatarSize || 60)),
                                        height: appendPx(isMobileLayout ? (data.avatarSize || 60) * 0.8 : (data.avatarSize || 60)),
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                                        order: isMobileLayout ? 1 : 2
                                    }}
                                />
                            )}
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: isMobileLayout ? '8px' : '6px',
                            maxWidth: isMobileLayout ? '100%' : '400px',
                            flex: isMobileLayout ? 'none' : 1,
                            justifyContent: isMobileLayout ? 'center' : 'flex-start', // Desktop: links, Mobile: center
                            order: isMobileLayout ? 2 : 1
                        }}>
                            {data.testimonials.map((_, index) => (
                                <button
                                    key={index}
                                    className="supreme-testimonial-sidebyside-line-indicator"
                                    style={{
                                        width: isMobileLayout ? '16px' : '20px',
                                        height: '2px',
                                        borderRadius: '1px',
                                        border: 'none',
                                        backgroundColor: index === activeTestimonialIndex ? '#ffffff' : 'rgba(255,255,255,0.4)',
                                        cursor: !isEditMode ? 'pointer' : 'default',
                                        transition: 'all 0.3s ease',
                                        opacity: index === activeTestimonialIndex ? 1 : 0.6,
                                        transform: index === activeTestimonialIndex ? 'scaleX(1.2)' : 'scaleX(1)'
                                    }}
                                    onClick={() => {
                                        if (!isEditMode) {
                                            setActiveTestimonialIndex(index);
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {/* Inline Below Quote */}
                {layoutConfig.displayType === 'inline-below' && data.testimonials && data.testimonials.length > 0 && (
                    <div style={{
                        width: '100%',
                        height: 'auto', // Nicht 100% Höhe
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '15px'
                    }}>
                        {/* Container mit Avatar und Info */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.3)',
                            borderRadius: isMobileLayout ? '8px' : '12px',
                            padding: isMobileLayout ? '12px 15px' : '15px 20px', // Schmaler
                            backdropFilter: 'blur(10px)',
                            maxWidth: isMobileLayout ? '90%' : '400px', // Schmaler
                            width: '100%'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: isMobileLayout ? 'column' : 'row',
                                alignItems: 'center',
                                gap: isMobileLayout ? '10px' : '15px', // Kleinere Gaps
                                textAlign: isMobileLayout ? 'center' : 'left'
                            }}>
                                {data.testimonials[activeTestimonialIndex]?.avatar && (
                                    <img 
                                        src={data.testimonials[activeTestimonialIndex].avatar}
                                        alt={data.testimonials[activeTestimonialIndex].name}
                                        className="supreme-testimonial-inline-below-avatar"
                                        style={{
                                            width: appendPx(isMobileLayout ? (data.avatarSize || 60) * 0.6 : (data.avatarSize || 60) * 0.7), // Kleiner
                                            height: appendPx(isMobileLayout ? (data.avatarSize || 60) * 0.6 : (data.avatarSize || 60) * 0.7),
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                                            flexShrink: 0
                                        }}
                                    />
                                )}

                                <div style={{ flex: 1 }}>
                                    <h4 style={{
                                        fontSize: appendPx(isMobileLayout ? (data.nameSize || 18) * 0.8 : (data.nameSize || 18) * 0.9), // Kleiner
                                        fontWeight: data.nameWeight || '600',
                                        fontFamily: data.nameFont || fonts.globalSmallFont,
                                        color: data.nameColor || '#ffffff',
                                        margin: '0 0 4px 0',
                                        textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
                                    }}>
                                        {data.testimonials[activeTestimonialIndex]?.name}
                                    </h4>
                                    {data.testimonials[activeTestimonialIndex]?.info && (
                                        <p style={{
                                            fontSize: appendPx(isMobileLayout ? (data.infoSize || 14) * 0.8 : (data.infoSize || 14) * 0.9),
                                            fontWeight: data.infoWeight || '400',
                                            fontFamily: data.infoFont || fonts.globalSmallFont,
                                            color: data.infoColor || '#cccccc',
                                            margin: '0',
                                            lineHeight: '1.3',
                                            textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none'
                                        }}>
                                            {data.testimonials[activeTestimonialIndex].info}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Striche Navigation unter dem Container */}
                        {data.testimonials.length > 1 && (
                            <div className="supreme-testimonial-inline-below-stripes" style={{
                                display: 'flex',
                                gap: isMobileLayout ? '8px' : '6px',
                                justifyContent: 'center'
                            }}>
                                {data.testimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        className="supreme-testimonial-inline-below-line-indicator"
                                        style={{
                                            width: isMobileLayout ? '16px' : '20px',
                                            height: '2px',
                                            borderRadius: '1px',
                                            border: 'none',
                                            backgroundColor: index === activeTestimonialIndex ? '#ffffff' : 'rgba(255,255,255,0.4)',
                                            cursor: !isEditMode ? 'pointer' : 'default',
                                            transition: 'all 0.3s ease',
                                            opacity: index === activeTestimonialIndex ? 1 : 0.6,
                                            transform: index === activeTestimonialIndex ? 'scaleX(1.2)' : 'scaleX(1)'
                                        }}
                                        onClick={() => {
                                            if (!isEditMode) {
                                                setActiveTestimonialIndex(index);
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Avatar Carousel */}
                {layoutConfig.displayType === 'avatar-carousel' && data.testimonials && data.testimonials.length > 0 && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        gap: isMobileLayout ? '15px' : '20px',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflowX: isMobileLayout ? 'auto' : 'visible',
                        padding: isMobileLayout ? '10px 0' : '0'
                    }}>
                        {data.testimonials.map((testimonial, index) => (
                            <div 
                                key={index}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: !isEditMode ? 'pointer' : 'default',
                                    opacity: index === activeTestimonialIndex ? 1 : 0.6,
                                    transform: index === activeTestimonialIndex ? 'scale(1.1)' : 'scale(1)',
                                    transition: 'all 0.3s ease',
                                    flexShrink: 0,
                                    minWidth: isMobileLayout ? '60px' : 'auto'
                                }}
                                onClick={() => {
                                    if (!isEditMode) {
                                        setActiveTestimonialIndex(index);
                                    }
                                }}
                            >
                                {testimonial.avatar && (
                                    <img 
                                        src={testimonial.avatar}
                                        alt={testimonial.name}
                                        style={{
                                            width: appendPx(isMobileLayout ? (data.avatarSize || 60) * 0.9 : (data.avatarSize || 60) * 1.2),
                                            height: appendPx(isMobileLayout ? (data.avatarSize || 60) * 0.9 : (data.avatarSize || 60) * 1.2),
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            boxShadow: index === activeTestimonialIndex ? 
                                                '0 6px 20px rgba(255, 255, 255, 0.3)' : 
                                                '0 4px 12px rgba(0, 0, 0, 0.2)',
                                            border: index === activeTestimonialIndex ? '3px solid rgba(255,255,255,0.8)' : 'none'
                                        }}
                                    />
                                )}
                                
                                <h4 style={{
                                    fontSize: appendPx(isMobileLayout ? (data.nameSize || 16) * 0.7 : (data.nameSize || 16) * 0.8),
                                    fontWeight: data.nameWeight || '600',
                                    fontFamily: data.nameFont || fonts.globalSmallFont,
                                    color: data.nameColor || '#ffffff',
                                    margin: isMobileLayout ? '6px 0 0 0' : '8px 0 0 0',
                                    textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                                    whiteSpace: 'nowrap',
                                    maxWidth: isMobileLayout ? '70px' : 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {testimonial.name}
                                </h4>
                            </div>
                        ))}
                    </div>
                )}

                {/* Single Avatar Focus */}
                {layoutConfig.displayType === 'single-avatar' && data.testimonials && data.testimonials.length > 0 && (
                    <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        gap: isMobileLayout ? '12px' : '15px'
                    }}>
                        {data.testimonials[activeTestimonialIndex]?.avatar && (
                            <img 
                                src={data.testimonials[activeTestimonialIndex].avatar}
                                alt={data.testimonials[activeTestimonialIndex].name}
                                style={{
                                    width: appendPx(isMobileLayout ? (data.avatarSize || 60) * 1.1 : (data.avatarSize || 60) * 1.4),
                                    height: appendPx(isMobileLayout ? (data.avatarSize || 60) * 1.1 : (data.avatarSize || 60) * 1.4),
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    boxShadow: '0 8px 24px rgba(255, 255, 255, 0.2)',
                                    border: isMobileLayout ? '3px solid rgba(255,255,255,0.9)' : '4px solid rgba(255,255,255,0.9)'
                                }}
                            />
                        )}
                        
                        <h4 style={{
                            fontSize: appendPx(isMobileLayout ? (data.nameSize || 20) * 0.9 : (data.nameSize || 20)),
                            fontWeight: data.nameWeight || '600',
                            fontFamily: data.nameFont || fonts.globalSmallFont,
                            color: data.nameColor || '#ffffff',
                            margin: '0',
                            textShadow: data.textShadow ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
                            maxWidth: isMobileLayout ? '200px' : 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {data.testimonials[activeTestimonialIndex]?.name}
                        </h4>

                        {data.testimonials.length > 1 && (
                            <div style={{
                                display: 'flex',
                                gap: isMobileLayout ? '6px' : '8px',
                                marginTop: isMobileLayout ? '8px' : '10px'
                            }}>
                                {data.testimonials.map((_, index) => (
                                    <button
                                        key={index}
                                        style={{
                                            width: isMobileLayout ? '8px' : '10px',
                                            height: isMobileLayout ? '8px' : '10px',
                                            borderRadius: '50%',
                                            border: 'none',
                                            backgroundColor: index === activeTestimonialIndex ? '#ffffff' : 'rgba(255,255,255,0.4)',
                                            cursor: !isEditMode ? 'pointer' : 'default',
                                            transition: 'all 0.3s ease',
                                            transform: index === activeTestimonialIndex ? 'scale(1.2)' : 'scale(1)'
                                        }}
                                        onClick={() => {
                                            if (!isEditMode) {
                                                setActiveTestimonialIndex(index);
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Quote Only - keine Attribution */}
                {layoutConfig.displayType === 'quote-only' && (
                    <div style={{ display: 'none' }}></div>
                )}

            </div>

        </div>
    );
}

export default SupremeTestimonial;